/**
 * Available payment options
 * ! This enum should be only used to communicate between application and API.
 *
 * Defines the selected payment method on the API side.
 * STRIPE - used for payments by Credit Card
 * CRYPTO - used for payments by Crypto
 * ALIPAY - used for payments by AliPay
 * PAYPAL - used for payments by PayPal
 * GIROPAY - used for payments by Giropay
 * SOFORT - used for payments by Sofort
 * IDEAL - used for payments by iDeal
 * BLIK - used for payments by Blik
 * P24 - used for payments by P24
 * WALLET - used for payments by the user's balance
 * BANK_TRANSFER - used for payments by manual bank wire transfer
 */
export enum PaymentOption {
  STRIPE = 'STRIPE',
  CRYPTO = 'CRYPTO',
  ALIPAY = 'ALIPAY',
  PAYPAL = 'PAYPAL',
  GIROPAY = 'GIROPAY',
  SOFORT = 'SOFORT',
  IDEAL = 'IDEAL',
  BLIK = 'BLIK',
  P24 = 'P24',
  WECHAT = 'WE_CHAT',
  WALLET = 'WALLET',
  KAKAO = 'KAKAO',
  NAVER = 'NAVER',
  PAYCO = 'PAYCO',
  SAMSUNG = 'SAMSUNG',
  BANK_TRANSFER = 'BANK_TRANSFER',
}

/**
 * Credit card types
 *
 * Defines which credit card type was used by the use.
 * Needs to be defined when user selects the CreditCard payment option.
 */
export type CreditCardType = 'new-card' | 'another-card' | 'existing-card';

/**
 * Top-up payment types
 *
 * Defines the available payment methods in Top-up process.
 */
export type TopUpPaymentOptionType =
  | CreditCardType
  | 'crypto'
  | 'alipay'
  | 'paypal'
  | 'giropay'
  | 'sofort'
  | 'ideal'
  | 'blik'
  | 'p24'
  | 'wechat'
  | 'kakao'
  | 'naver'
  | 'payco'
  | 'samsung'
  | 'bank-transfer';

/**
 * Checkout payment types
 *
 * Defines the available payment method in Checkout process.
 */
export type PaymentOptionType = TopUpPaymentOptionType | 'balance';

/**
 * Event type for Payments Broadcast channel
 */
export type PaymentCallbackEvent = {
  isPaymentFinished: boolean;
};

/**
 * Payment redirect metadata obtained from API
 */
export type PaymentRedirectMetadata = {
  /**
   * Payment metadata type
   */
  type: 'redirect';

  /**
   * URL to which SPA should redirect user on success
   */
  data: string;
};

/**
 * Payment token metadata obtained from API
 */
export type PaymentTokenMetadata = {
  /**
   * Payment metadata type
   */
  type: 'token';

  /**
   * PaymentIntent token required by Stripe
   */
  data: string;
};

/**
 * Payment bank transfer metadata obtained from API
 *
 * The recipient bank account is fixed (sourced from env config on the backend, not per-order).
 * `data.reference` is the order's short code used to auto-match the incoming wire transfer - it must be
 * shown to the customer as required transfer memo/reference text.
 */
export type PaymentBankDetailsMetadata = {
  /**
   * Payment metadata type
   */
  type: 'bank_details';

  data: {
    accountHolderName: string;
    accountNumber: string;
    bicSwift: string;
    currency: string;
    reference: string;
  };
};

export type PaymentMetadata = PaymentRedirectMetadata | PaymentTokenMetadata;

/**
 * Payment metadata returned by the Top-up flow.
 *
 * Bank transfer is only ever a Top-up payment option (not a Checkout one), so `PaymentBankDetailsMetadata`
 * is kept out of the shared `PaymentMetadata` union to avoid affecting Checkout's payment handling.
 */
export type TopUpMetadata = PaymentMetadata | PaymentBankDetailsMetadata;
